import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Plus } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { inAppHref } from './inAppHref'
import remarkGfm from 'remark-gfm'

import type { Insight, InsightMessage } from '../app/api/insightsApi'
import {
  AttachmentCards,
  MAX_EMAIL_ATTACHMENT_TOTAL,
  addAttachmentFiles,
} from '../app/components/attachments/AttachmentCards'
import { AuthedImage } from '../app/components/attachments/AuthedImage'
import { AttachmentLink, isAttachmentLink } from '../app/components/attachments/AttachmentLink'
import { ChatComposer } from './CodexChatRail'
import { useInsightChat } from './useInsightChat'
import styles from './InsightChatWorkspace.module.scss'

const INBOX_IMAGE_SRC = /^\/api\/internal\/insight-attachments\/[0-9a-f]{32}$/

function AssistantImage({ src, alt }: ComponentProps<'img'>) {
  if (!src || !INBOX_IMAGE_SRC.test(src)) return <span>{alt || 'Immagine non disponibile'}</span>
  return <AuthedImage src={src} alt={alt || ''} />
}

function AssistantLink({ href, children }: ComponentProps<'a'>) {
  if (isAttachmentLink(href)) return <AttachmentLink href={href}>{children}</AttachmentLink>
  const path = inAppHref(href)
  if (path) return <Link to={path}>{children}</Link>
  return <a href={href} target="_blank" rel="noreferrer">{children}</a>
}

function isAssistant(source: InsightMessage['source']) {
  return source === 'agent' || source === 'voice'
}

function userDeliveryLabel(source: InsightMessage['source']) {
  if (source === 'email') return 'via email'
  return source === 'voice' ? 'via telefono' : 'in Luma'
}

function messageLabel(message: TranscriptMessage, agentName: string) {
  if (message.role === 'assistant') {
    return message.source === 'voice' ? `${agentName} · via telefono` : agentName
  }
  return `Tu · ${userDeliveryLabel(message.source)}`
}

function insightStatus(insight: Insight) {
  if (!insight.triage) return 'Da fare'
  if (insight.triage.state === 'done') return 'Completata'
  if (insight.triage.state === 'snoozed') return 'Posticipata'
  return 'Scartata'
}

function originalText(insight: Insight, role: 'user' | 'assistant') {
  const parts = [...insight.body]
  if (role === 'assistant' && insight.numbers.length > 0) {
    parts.push(insight.numbers.map((number) => `- **${number.label}:** ${number.value}`).join('\n'))
  }
  if (role === 'assistant' && insight.action) parts.push(insight.action)
  return parts.filter(Boolean).join('\n\n')
}

type TranscriptMessage = {
  id: string
  role: 'user' | 'assistant'
  text: string
  source: InsightMessage['source']
  createdAt: string
  attachments?: InsightMessage['attachments']
}

function transcript(insight: Insight): TranscriptMessage[] {
  const originalRole = insight.kind === 'question' && insight.origin !== 'agent'
    ? 'user'
    : 'assistant'
  return [{
    id: 'original',
    role: originalRole,
    text: originalText(insight, originalRole),
    source: insight.window === 'Asked by email' || insight.window === 'Welcome email'
      ? 'email'
      : originalRole === 'assistant' ? 'agent' : 'app',
    createdAt: insight.computedAt,
    attachments: insight.attachments,
  }, ...insight.messages.map((message, index) => ({
    id: `${message.createdAt}-${index}`,
    role: isAssistant(message.source) ? 'assistant' as const : 'user' as const,
    text: message.body,
    source: message.source,
    createdAt: message.createdAt,
    attachments: message.attachments,
  }))]
}

function attachmentItems(attachments: InsightMessage['attachments']) {
  return (attachments ?? []).map((attachment) => ({
    key: attachment.id,
    filename: attachment.filename,
    contentType: attachment.contentType,
    size: attachment.size,
    url: `/api/internal/insight-attachments/${attachment.id}`,
  }))
}

export function InsightChatWorkspace({
  fingerprint,
  agentName,
  onBack,
  onNewConversation,
}: {
  fingerprint: string
  agentName: string
  onBack: () => void
  onNewConversation: () => void
}) {
  const { insight, loading, running, error, send } = useInsightChat(fingerprint)
  const [files, setFiles] = useState<File[]>([])
  const [attachmentError, setAttachmentError] = useState('')
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const messages = useMemo(() => insight ? transcript(insight) : [], [insight])

  useEffect(() => {
    setFiles([])
    setAttachmentError('')
  }, [fingerprint])

  useLayoutEffect(() => {
    const element = scrollRef.current
    if (element) element.scrollTop = element.scrollHeight
  }, [messages, running])

  const addFiles = useCallback((next: File[]) => {
    setFiles((current) => {
      const result = addAttachmentFiles(current, next, MAX_EMAIL_ATTACHMENT_TOTAL)
      setAttachmentError(result.error)
      return result.files
    })
  }, [])

  const handleSend = useCallback(async (body: string, nextFiles: File[] = []) => {
    if (await send(body, nextFiles)) {
      setFiles([])
      setAttachmentError('')
    }
  }, [send])

  return (
    <section className={styles.workspace} aria-label={`Chat con ${agentName}`}>
      <header className={styles.header}>
        <button type="button" className={styles.headerButton} aria-label="Torna a Luma" onClick={onBack}>
          <ArrowLeft size={18} aria-hidden />
        </button>
        <span className={styles.identity}>Chat con {agentName}</span>
        <button type="button" className={styles.headerButton} aria-label="Nuova conversazione" onClick={onNewConversation}>
          <Plus size={17} aria-hidden />
        </button>
      </header>

      {insight ? (
        <div className={styles.topic}>
          <strong>{insight.title}</strong>
          <span>{insight.window === 'Asked by email' ? 'via email' : 'Conversazione'} · {insightStatus(insight)}</span>
        </div>
      ) : null}

      <div ref={scrollRef} className={styles.conversation}>
        <div className={styles.messageList}>
          {loading ? <p className={styles.state}>Caricamento…</p> : null}
          {messages.map((message) => (
            <article key={message.id} className={styles.turn} data-role={message.role}>
              <span className={styles.messageMeta}>
                {messageLabel(message, agentName)}
              </span>
              {message.role === 'assistant' ? (
                <div className={styles.assistantMessage}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{ img: AssistantImage, a: AssistantLink }}
                  >
                    {message.text}
                  </ReactMarkdown>
                  <AttachmentCards items={attachmentItems(message.attachments)} />
                </div>
              ) : (
                <div className={styles.userMessage}>
                  {message.text ? <p>{message.text}</p> : null}
                  <AttachmentCards items={attachmentItems(message.attachments)} />
                </div>
              )}
            </article>
          ))}
          {error ? <p className={styles.error} role="alert">{error}</p> : null}
          <div className={styles.nowLine} role="status" aria-hidden={running ? undefined : true}>
            {running ? <><span className="chat-breathing-dot" /> <span>Sto lavorando…</span></> : null}
          </div>
        </div>
      </div>

      <div className={styles.composerDock}>
        <ChatComposer
          disabled={running || loading || !insight}
          onSend={handleSend}
          files={files}
          attachmentError={attachmentError}
          onFilesChange={setFiles}
          onAddFiles={addFiles}
        />
      </div>
    </section>
  )
}
